import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Wind, FileText, Calendar, X } from 'lucide-react';
import { Card, Button } from '../design-system';
import TypingSpeechBubble from './TypingSpeechBubble';
import './MascotHub.css';

// Global flag to track if greeting has been completed (persists across remounts)
let globalGreetingCompleted = false;

// Custom comparison function for React.memo
// Only re-render if props actually changed meaningfully
const arePropsEqual = (prevProps, nextProps) => {
  // Check primitive props
  if (prevProps.userName !== nextProps.userName) {
    return false;
  }
  if (prevProps.showMoodTracker !== nextProps.showMoodTracker) {
    return false;
  }
  if (prevProps.showGrounding !== nextProps.showGrounding) {
    return false;
  }
  if (prevProps.moodAlreadySubmitted !== nextProps.moodAlreadySubmitted) {
    return false;
  }
  
  // For component props, check if they're both null or both not null
  // (React elements always have new identities, so we check existence)
  if ((prevProps.moodTrackerComponent === null) !== (nextProps.moodTrackerComponent === null)) {
    return false;
  }
  if ((prevProps.groundingComponent === null) !== (nextProps.groundingComponent === null)) {
    return false;
  }
  // Always re-render if goalsComponent changes (it's a memoized JSX element, so reference changes mean content changed)
  if (prevProps.goalsComponent !== nextProps.goalsComponent) {
    return false;
  }
  
  // Check callbacks - if they changed, we need to re-render
  if (prevProps.onGroundingClick !== nextProps.onGroundingClick) {
    return false;
  }
  if (prevProps.onMoodComplete !== nextProps.onMoodComplete) {
    return false;
  }
  if (prevProps.onTasksClick !== nextProps.onTasksClick) {
    return false;
  }
  if (prevProps.onAppointmentsClick !== nextProps.onAppointmentsClick) {
    return false;
  }
  if (prevProps.onCloseGrounding !== nextProps.onCloseGrounding) {
    return false;
  }
  
  return true;
};

const MascotHub = memo(({ 
  onGroundingClick, 
  onMoodComplete,
  onTasksClick,
  onAppointmentsClick,
  userName,
  showMoodTracker,
  moodTrackerComponent,
  showGrounding,
  groundingComponent,
  goalsComponent,
  onCloseGrounding,
  moodAlreadySubmitted
}) => {
  // Initialize greetingComplete based on global flag to prevent restart on remount
  const [greetingComplete, setGreetingComplete] = useState(() => {
    return globalGreetingCompleted;
  });
  const [moodSubmitted, setMoodSubmitted] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [bubbleFadingOut, setBubbleFadingOut] = useState(false);
  
  
  // Track if options have been initialized to prevent duplicate initialization
  const optionsInitializedRef = useRef(false);
  // Track if greeting has been shown to prevent remounting TypingSpeechBubble
  const greetingShownRef = useRef(false);
  // Capture initial userName to prevent remount when userName prop changes
  const initialUserNameRef = useRef(userName || 'there');
  
  
  // Update initial userName only if it hasn't been captured yet
  useEffect(() => {
    if (!greetingShownRef.current && userName && userName !== initialUserNameRef.current && initialUserNameRef.current === 'there') {
      initialUserNameRef.current = userName;
    }
  }, [userName]);

  // Memoize callback to prevent TypingSpeechBubble from remounting
  const handleGreetingComplete = useCallback(() => {
    greetingShownRef.current = true;
    globalGreetingCompleted = true; // Set global flag first
    setBubbleFadingOut(true); // Mark bubble as fading out
    
    // Wait for bubble fade-out animation to complete before changing layout
    // This prevents the bubble from shifting when the layout changes
    setTimeout(() => {
      setGreetingComplete(true);
      setBubbleFadingOut(false);
      // Always call onMoodComplete to signal animation is done
      // (This is used for loading state tracking even when moodAlreadySubmitted is true)
      if (onMoodComplete) {
        onMoodComplete();
      }
      // The consolidated useEffect will handle showing mood tracker or options
      // based on moodAlreadySubmitted state
    }, 500); // Wait for fade-out animation (0.5s) to complete
  }, [onMoodComplete]);

  const handleMoodSubmitted = () => {
    setMoodSubmitted(true);
    // After 2 seconds, transition to options view
    setTimeout(() => {
      setIsTransitioning(true);
      // Change text immediately, fade out mood tracker
      setTimeout(() => {
        setShowOptions(true);
        setIsTransitioning(false); // Clear transitioning state
        // Don't call onMoodComplete here - we want to keep the layout but hide the tracker
      }, 500); // Half second for fade out transition
    }, 2000); // 2 seconds after mood submission
  };

  // Effect to handle greeting completion callback when greeting is already complete
  // This ensures onMoodComplete is called even if greeting was skipped due to global flag
  // This is critical for loading states where the callback must be called to proceed
  useEffect(() => {
    // If greeting is already complete (from global flag) but callback hasn't been called yet
    // This can happen when component remounts and globalGreetingCompleted is true
    if (greetingComplete && onMoodComplete) {
      // Check if we've already called the callback for this instance
      // Use a ref to track this to prevent multiple calls
      if (!greetingShownRef.current) {
        // Call the callback immediately since greeting was already shown before
        // Use a small delay to ensure component is fully mounted and state is stable
        const timeoutId = setTimeout(() => {
          if (onMoodComplete) {
            onMoodComplete();
          }
          greetingShownRef.current = true;
        }, 50); // Small delay to ensure component is ready
        return () => clearTimeout(timeoutId);
      }
    }
  }, [greetingComplete, onMoodComplete]);

  // Consolidated effect to handle options view initialization
  // This prevents flickering by handling all state transitions in one place
  useEffect(() => {
    // Skip if already showing a specific view (grounding, tasks, or mood tracker)
    if (showGrounding || showMoodTracker) {
      return;
    }
    
    // CRITICAL: Don't change layout while bubble is fading out
    // This prevents the speech bubble from appearing to "jump" from center to right
    // when the layout transitions from centered (greeting) to left-aligned (options).
    // The bubble is positioned relative to .mascot-container, so if the container
    // moves during fade-out, the bubble would appear to move with it.
    // Solution: Lock layout changes until fade-out completes (500ms)
    if (bubbleFadingOut) {
      return;
    }
    
    // If mood was already submitted and greeting is complete, show options
    if (moodAlreadySubmitted && greetingComplete) {
      // Only update once to prevent flickering - use ref to track
      if (!optionsInitializedRef.current) {
        optionsInitializedRef.current = true;
        setMoodSubmitted(true);
        setShowOptions(true);
      }
      return;
    }
    
    // If mood was submitted (just now, not from API), show options
    if (moodSubmitted && !showOptions && !moodAlreadySubmitted) {
      setShowOptions(true);
      return;
    }
    
    // Don't reset states if we're still waiting for mood check to complete
    // Only reset if we know mood wasn't submitted AND greeting is complete
    if (!moodSubmitted && !moodAlreadySubmitted && greetingComplete && showOptions) {
      // This means mood wasn't submitted and we shouldn't show options
      // But only reset if we're certain (greeting complete = mood check should be done)
        setShowOptions(false);
        setIsTransitioning(false);
      }
  }, [
    moodAlreadySubmitted, 
    greetingComplete, 
    showMoodTracker, 
    showGrounding, 
    showOptions, 
    moodSubmitted,
    bubbleFadingOut  // Add bubbleFadingOut to dependencies
  ]);

  // Determine if we're in the main layout (after greeting)
  const isMainLayout = (showMoodTracker || (showOptions && !bubbleFadingOut) || showGrounding);
  const isGreetingMode = (!showMoodTracker && !showGrounding && !showOptions) || bubbleFadingOut;
  
  // Show welcome message during greeting mode
  // Always show during greeting mode if greeting is not complete, or if bubble is fading out
  // This ensures the message shows even if the component remounts during loading
  const shouldShowWelcomeMessage = isGreetingMode && (!greetingComplete || bubbleFadingOut);

  return (
    <Card variant="default" padding="large" className={`mascot-hub-container ${isMainLayout ? 'mood-tracker-mode' : ''}`}>
      {/* Main Content Area - Two columns on desktop, stacked on mobile */}
      <div className={`mascot-hub-main-content ${isMainLayout ? 'mood-tracker-layout' : ''} ${isGreetingMode ? 'greeting-mode-active' : ''}`}>
        {/* Left Column: Mascot Container - Always visible after greeting */}
        {!isGreetingMode && (
          <div className="mascot-container-column">
            <div className="mascot-image-wrapper">
              <img 
                src="/images/mascot/mascot.gif" 
                alt="Therapy Mascot" 
                className="mascot-image"
              />
            </div>
          </div>
        )}

        {/* Right Column: Action Container - Scrollable content */}
        {!isGreetingMode && (
          <div className="action-container-column">
            {/* Mood Tracker - Shown when survey is pending */}
            {showMoodTracker && !showOptions && !showGrounding && moodTrackerComponent && (
              <div 
                className={`mood-tracker-container ${isTransitioning ? 'fade-out' : ''}`} 
                style={isTransitioning || showOptions ? { display: 'none' } : {}}
              >
                {React.cloneElement(moodTrackerComponent, { onMoodSubmitted: handleMoodSubmitted })}
              </div>
            )}

            {/* Tasks/Goals - Shown after mood survey */}
            {showOptions && !showGrounding && (
              <div className="options-content fade-in">
                {/* Title */}
                <div className="options-title-container">
                  <h2 className="options-title">Time for making progress</h2>
                </div>

                {/* Goals Heading */}
                <div className="w-full mb-3">
                  <h3 className="text-lg font-semibold text-gray-700">Your goals</h3>
                </div>

                {/* Goals will be shown here */}
                {goalsComponent || <div>No goals component</div>}
              </div>
            )}

            {/* Grounding Component */}
            {showGrounding && groundingComponent && (
              <div className="grounding-container">
                {groundingComponent}
              </div>
            )}
          </div>
        )}

        {/* Greeting Mode - Centered mascot with speech bubble (only during initial greeting) */}
        {isGreetingMode && (
          <div className={`mascot-container greeting-mode ${bubbleFadingOut ? 'bubble-fading-out' : ''}`}>
            {shouldShowWelcomeMessage ? (
              <TypingSpeechBubble 
                key="welcome-bubble"
                message="Welcome {user}! Another step towards progress."
                userName={initialUserNameRef.current}
                onComplete={handleGreetingComplete}
                isFadingOut={bubbleFadingOut}
              />
            ) : null}
            <div className="mascot-image-wrapper">
              <img 
                src="/images/mascot/mascot.gif" 
                alt="Therapy Mascot" 
                className="mascot-image"
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer Section: Grounding Button - Always at bottom */}
      {((greetingComplete && !bubbleFadingOut) || showMoodTracker || showOptions || showGrounding) && (
        <div className="mascot-hub-footer">
          <button
            onClick={onGroundingClick}
            className="action-button-circle action-button-grounding"
            aria-label="Grounding Exercise"
          >
            <Wind className="action-icon" />
          </button>
        </div>
      )}
    </Card>
  );
}, arePropsEqual);

MascotHub.displayName = 'MascotHub';

export default MascotHub;

